import { Fragment, lazy } from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import { isNil } from "lodash";
import { createContext } from "react";
import AuthGuard from "src/components/AuthGuard";
import GuestGuard from "src/components/GuestGuard";
import type { AuthContextValue } from "src/contexts/JWTAuthContext";
import { type RouteMetadata, RouteMetadataProvider } from "src/contexts/RouteMetadataContext";
import { AccountLayout } from "src/layouts/AccountLayout";
import AdminAnalyzeLayout from "src/layouts/AdminAnalyzeLayout";
import { DashboardLayout } from "src/layouts/DashboardLayout";
import { PageEnum } from "src/utils/tracking/types/pageEnum";
import { GlobalLayout } from "src/views-ds/layout";
import { HomePageLayout } from "src/views-ds/layout/HomePageLayout";
import { accountRoutes } from "src/views/account/routes";
import AuthGuardAdmin from "./components/AuthGuardAdmin";
import AuthGuardNotAdmin from "./components/AuthGuardNotAdmin";
import { DomainDemo } from "./domains/components/DomainDemo";
import { AdminLayout } from "./layouts/AdminLayout";
import AdminMonitoringLayout from "./layouts/AdminMonitoringLayout";
import OrganizationLayout from "./layouts/OrganizationLayout";
import ParameterLayout from "./layouts/ParameterLayout";
import SimpleTopBarLayout from "./layouts/SimpleTopBarLayout";
import SimpleViewLayout from "./layouts/SimpleViewLayout";
import {
    PATH_AGENDA,
    PATH_DASHBOARD,
    PATH_HOME,
    PATH_MARKETING_ACTION,
    PATH_OFFER,
    PATH_POS_LIST,
    PATH_POS_ONBOARDING,
    PATH_POS_PARAMETER,
    PATH_SUPERADMIN,
    PATH_SUPERADMIN_ANALYZE,
    PATH_SUPERADMIN_DEBUG,
    PATH_SUPERADMIN_MONITORING,
    PATH_SUPERADMIN_SUPERPOWER,
    PATH_WELCOME_PAGE,
} from "./routesConstants";
import { organizationRoutes, PATH_ORGANIZATION } from "./views/organization/routes";
import { rankingRoutes } from "./views/ranking/routes";
import { sponsoredAdsRoutes } from "./views/sponsored-ads/routes";

type Routes = {
    exact?: boolean;
    path?: string | string[];
    guard?: any;
    layout?: any;
    component?: any;
    routes?: Routes;
    /**
     * Documentation gestion des features beta
     * @see https://www.notion.so/flynt/Gestion-des-features-en-Beta-WIP-426f24cb68534ce6b1c855094141e2f5
     */
    betaComponent?: any;
    allowedUserIdVariableName?: string;
    betaFeatureConfig?: {
        featureName: string;
        envVariable: string;
    }[];
    /**
     * Custom metadata that can be accessed in components
     */
    meta?: RouteMetadata;
}[];

export const BetaContext = createContext({});

// ACTIVE BETA FEATURE
// export const STATIC_PERIOD_FEATURE_NAME = "STATIC_PERIOD_FEATURE";
// export const ENV_STATIC_PERIOD_USER_ID_BETA_ALLOWED = "VITE_FLYNT_STATIC_PERIOD_USER_ID_BETA_ALLOWED";

// export const STATIC_PERIOD_BETA_CONFIG = {
//     featureName: STATIC_PERIOD_FEATURE_NAME,
//     envVariable: ENV_STATIC_PERIOD_USER_ID_BETA_ALLOWED,
// };

export const MARKETING_ACTION_TEMPLATE_FEATURE_NAME = "MARKETING_ACTION_TEMPLATE";
export const ENV_MARKETING_ACTION_TEMPLATE_USER_ID_BETA_ALLOWED =
    "VITE_ACTIVATE_USER_ID_MARKETING_ACTION_TEMPLATE";

export const MARKETING_ACTION_TEMPLATE_BETA_CONFIG = {
    featureName: MARKETING_ACTION_TEMPLATE_FEATURE_NAME,
    envVariable: ENV_MARKETING_ACTION_TEMPLATE_USER_ID_BETA_ALLOWED,
};

export const DASHBOARD_GLOBAL_TREND_NAME = "DASHBOARD_GLOBAL_TREND";
export const ENV_DASHBOARD_GLOBAL_TREND_USER_ID_BETA_ALLOWED =
    "VITE_ACTIVATE_USER_ID_DASHBOARD_GLOBAL_TREND";
export const DASHBOARD_GLOBAL_TREND_BETA_CONFIG = {
    featureName: DASHBOARD_GLOBAL_TREND_NAME,
    envVariable: ENV_DASHBOARD_GLOBAL_TREND_USER_ID_BETA_ALLOWED,
};

export const renderRoutes = (
    routes: Routes = [],
    auth: AuthContextValue,
    openAllBeta: boolean
): JSX.Element => (
    <Switch>
        {routes.map((route, i) => {
            const Guard = route.guard || Fragment;
            const Layout = route.layout || Fragment;
            const Component = route.component;

            /**
             * BETA PAGE MANAGEMENT
             */
            const BetaComponent = route.betaComponent;
            const allowedUserId =
                route.allowedUserIdVariableName && import.meta.env[route.allowedUserIdVariableName]
                    ? import.meta.env[route.allowedUserIdVariableName]?.split(";")
                    : [];
            const FinalComponent =
                (openAllBeta || allowedUserId?.includes(auth.user?.id?.toString())) &&
                !isNil(BetaComponent)
                    ? BetaComponent
                    : Component;

            /**
             * BETA FEATURE MANAGEMENT
             */

            const betaContextProviderValue = route?.betaFeatureConfig?.reduce((acc, config) => {
                const featureAllowedUserId =
                    config.envVariable && import.meta.env[config.envVariable]
                        ? import.meta.env[config.envVariable]?.split(";")
                        : [];

                acc[config.featureName] = openAllBeta
                    ? true
                    : featureAllowedUserId?.includes(auth.user?.id?.toString());
                return acc;
            }, {});

            return (
                <Route
                    key={i}
                    path={route.path}
                    exact={route.exact}
                    render={(props) => {
                        return (
                            <RouteMetadataProvider initialMetadata={route.meta || {}}>
                                <Guard>
                                    <BetaContext.Provider value={betaContextProviderValue}>
                                        <Layout>
                                            {route.routes &&
                                                renderRoutes(route.routes, auth, openAllBeta)}
                                            {!route.routes && <FinalComponent {...props} />}
                                        </Layout>
                                    </BetaContext.Provider>
                                </Guard>
                            </RouteMetadataProvider>
                        );
                    }}
                />
            );
        })}
    </Switch>
);

const routes: Routes = [
    {
        exact: true,
        path: "/404",
        component: lazy(() => import("src/views/errors/NotFoundView")),
    },
    {
        exact: true,
        path: "/error",
        component: lazy(() => import("src/views/errors/ErrorView")),
    },
    {
        exact: true,
        path: "/403",
        component: lazy(() => import("src/views/errors/NotAllowedView")),
    },
    {
        exact: true,
        path: "/coming",
        component: lazy(() => import("src/views/errors/ToComeView")),
    },
    ...(import.meta.env.VITE_MAINTENANCE_ROUTES
        ? import.meta.env.VITE_MAINTENANCE_ROUTES.split(";")
        : ["/maintenance"]
    ).map((maintenanceRoutePath) => ({
        exact: false,
        path: maintenanceRoutePath,
        component: lazy(() => import("src/views/errors/UnderMaintenanceView")),
    })),
    {
        exact: true,
        guard: GuestGuard,
        path: "/",
        component: lazy(() => import("src/views/auth/login-view")),
    },
    {
        exact: true,
        guard: GuestGuard,
        path: "/login",
        component: lazy(() => import("src/views/auth/login-view")),
    },
    {
        exact: true,
        path: "/login-unprotected",
        component: lazy(() => import("src/views/auth/login-view")),
    },
    {
        exact: true,
        guard: GuestGuard,
        path: "/register",
        component: lazy(() => import("src/views/auth/RegisterView")),
    },
    {
        exact: true,
        path: "/register-unprotected",
        component: lazy(() => import("src/views/auth/RegisterView")),
    },
    {
        exact: true,
        path: "/forget",
        component: lazy(() => import("src/views/auth/forget/forget-view/index")),
    },
    {
        exact: true,
        path: "/thanks",
        component: lazy(() => import("src/views/auth/forget/thanks-view")),
    },
    {
        exact: true,
        path: "/account-creation/steps",
        component: lazy(() => import("src/views/onboarding/onboarding-view")),
    },
    {
        layout: SimpleTopBarLayout,
        exact: true,
        path: `${PATH_POS_ONBOARDING}/:organizationId`,
        component: lazy(() => import("src/views/onboarding/pos-onboarding-view")),
    },

    {
        path: PATH_ORGANIZATION,
        guard: AuthGuardNotAdmin,
        layout: OrganizationLayout,
        routes: [
            {
                path: organizationRoutes.possView(),
                component: lazy(() => import("src/views/pos/PosListView")),
            },
            {
                exact: true,
                path: organizationRoutes.detailsView(),
                component: lazy(() => import("src/views/organization/OrganizationDetailsView")),
            },
            {
                exact: true,
                path: organizationRoutes.billingView(),
                component: lazy(() => import("src/views/organization/OrganizationBillingView")),
            },
            {
                exact: true,
                path: organizationRoutes.invoicesListView(),
                component: lazy(
                    () => import("src/views-ds/organization/organization-invoices-view")
                ),
            },
            {
                path: organizationRoutes.externalToolsView(),
                component: lazy(() => import("src/views/organization/OrganizationExternalPosView")),
            },
        ],
    },
    {
        path: `${PATH_POS_LIST}`,
        guard: AuthGuardNotAdmin,
        layout: SimpleViewLayout,
        routes: [
            {
                exact: true,
                path: `${PATH_POS_LIST}/poss*`,
                component: lazy(() => import("src/views/pos/PosListView")),
            },
        ],
    },
    {
        path: "/app/account",
        guard: AuthGuard,
        layout: AccountLayout,
        routes: [
            {
                exact: true,
                path: accountRoutes.profileView(),
                component: lazy(() => import("src/views/account/AccountView")),
            },
            {
                exact: true,
                path: accountRoutes.passwordView(),
                component: lazy(() => import("src/views/account/PasswordView")),
            },
            {
                exact: true,
                path: accountRoutes.reportsView(),
                component: lazy(() => import("src/views/account/PreferencesView")),
            },
        ],
    },
    {
        path: PATH_SUPERADMIN,
        guard: AuthGuardAdmin,
        layout: AdminLayout,
        routes: [
            {
                exact: true,
                path: PATH_SUPERADMIN,
                component: () => <Redirect to={`${PATH_SUPERADMIN}/organization`} />,
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN}/organization`,
                component: lazy(
                    () => import("src/views/admin/AdminView/organization/organization-list-view")
                ),
            },

            {
                exact: true,
                path: `${PATH_SUPERADMIN}/monitoring`,
                component: lazy(() => import("src/views/admin/AdminView/monitoring")),
            },

            {
                exact: true,
                path: `${PATH_SUPERADMIN}/organization/:id`,
                component: lazy(
                    () => import("src/views/admin/AdminView/organization/organization-detail-view")
                ),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN}/pos/:id`,
                component: lazy(() => import("src/views/admin/AdminView/pos/pos-detail-view")),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN}/pos/:id/posconnection`,
                component: lazy(
                    () =>
                        import(
                            "src/views/admin/AdminView/pos/pos-connection-view/PosConnectionView"
                        )
                ),
            },

            {
                exact: true,
                path: `${PATH_SUPERADMIN}/pos`,
                component: lazy(() => import("src/views/admin/AdminView/pos/pos-list-view")),
            },

            {
                exact: true,
                path: `${PATH_SUPERADMIN}/pos-group`,
                component: lazy(
                    () => import("src/views/admin/AdminView/pos-group/pos-groups-view")
                ),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN}/pos-group/create`,
                component: lazy(
                    () => import("src/views/admin/AdminView/pos-group/pos-group-create-view")
                ),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN}/pos-group/:id`,
                component: lazy(
                    () => import("src/views/admin/AdminView/pos-group/pos-group-details-view")
                ),
            },

            {
                exact: true,
                path: `${PATH_SUPERADMIN}/user/create`,
                component: lazy(() => import("src/views/admin/AdminView/user/UserCreateView")),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN}/user/:id`,
                component: lazy(() => import("src/views/admin/AdminView/user/UserDetailsView")),
            },

            {
                exact: true,
                path: `${PATH_SUPERADMIN}/user`,
                component: lazy(() => import("src/views/admin/AdminView/user/UserListView")),
            },

            {
                exact: true,
                path: `${PATH_SUPERADMIN}/account-creation`,
                component: lazy(
                    () => import("src/views/admin/AdminView/account/accounts-creation-view")
                ),
            },

            {
                exact: true,
                path: `${PATH_SUPERADMIN}/non-reg`,
                component: lazy(() => import("src/views/admin/AdminView/non-reg")),
            },
        ],
    },
    {
        path: PATH_SUPERADMIN_MONITORING,
        guard: AuthGuardAdmin,
        layout: AdminMonitoringLayout,
        routes: [
            {
                exact: true,
                path: `${PATH_SUPERADMIN_MONITORING}/dashboard`,
                component: lazy(
                    () =>
                        import(
                            "src/views/admin/AdminView/admin-monitoring/AdminMonitoringEmptyPage"
                        )
                ),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN_MONITORING}/dashboard/:organizationId`,
                component: lazy(() => import("src/views/admin/AdminView/admin-monitoring")),
            },
        ],
    },
    {
        path: PATH_SUPERADMIN_DEBUG,
        guard: AuthGuardAdmin,
        layout: GlobalLayout,
        routes: [
            {
                exact: true,
                path: `${PATH_SUPERADMIN_DEBUG}/dashboard`,
                component: lazy(() => import("src/views/admin/AdminView/admin-debug")),
            },
        ],
    },
    {
        path: PATH_SUPERADMIN_ANALYZE,
        guard: AuthGuardAdmin,
        layout: AdminAnalyzeLayout,
        routes: [
            {
                exact: true,
                path: `${PATH_SUPERADMIN_ANALYZE}/dashboard`,
                component: lazy(() => import("src/views/admin/AdminView/dashboard")),
                betaFeatureConfig: [DASHBOARD_GLOBAL_TREND_BETA_CONFIG],
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN_ANALYZE}/ope-special`,
                component: lazy(() => import("src/views/admin/AdminView/operation-special-view")),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN_ANALYZE}/platform-pos-list`,
                component: lazy(() => import("src/views/admin/AdminView/platform-pos-list-view")),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN_ANALYZE}/export-list`,
                component: lazy(() => import("src/views/admin/AdminView/export-list-view")),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN_ANALYZE}/agenda`,
                component: lazy(() => import("src/views/admin/AdminView/agenda")),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN_ANALYZE}/detail/offer`,
                component: lazy(
                    () => import("src/views/admin/AdminView/detail/admin-offer-detail-view")
                ),
            },
            {
                exact: true,
                path: `${PATH_SUPERADMIN_ANALYZE}/detail/sponsored-ad`,
                component: lazy(
                    () => import("src/views/admin/AdminView/detail/admin-sponsored-ad-detail-view")
                ),
            },
        ],
    },
    {
        path: PATH_SUPERADMIN_SUPERPOWER,
        guard: AuthGuardAdmin,
        layout: GlobalLayout,
        routes: [
            {
                exact: true,
                path: `${PATH_SUPERADMIN_SUPERPOWER}/dashboard`,
                component: () => <Redirect to={PATH_HOME} />,
                betaComponent: lazy(() => import("src/views/admin/AdminView/admin-superpower")),
                allowedUserIdVariableName: "VITE_ACTIVATE_USER_ID_SUPERPOWER_TAB",
            },
        ],
    },
    {
        path: PATH_POS_PARAMETER,
        guard: AuthGuardNotAdmin,
        layout: ParameterLayout,
        routes: [
            {
                exact: true,
                path: `${PATH_POS_PARAMETER}/:currentTab`,
                component: lazy(() => import("src/views/pos/pos-details-view")),
            },
        ],
    },
    {
        path: PATH_MARKETING_ACTION,
        guard: AuthGuardNotAdmin,
        layout: DashboardLayout,
        routes: [
            // Marketing actions (offers & ads & duo)
            {
                exact: true,
                path: PATH_MARKETING_ACTION,
                component: lazy(
                    () => import("src/views/marketing-actions/marketing-actions-create-view")
                ),
                betaFeatureConfig: [MARKETING_ACTION_TEMPLATE_BETA_CONFIG],
            },
        ],
    },

    {
        path: rankingRoutes.root(),
        guard: AuthGuardNotAdmin,
        layout: DashboardLayout,
        routes: [
            {
                exact: true,
                path: rankingRoutes.root(),
                component: lazy(() => import("src/views/ranking/ranking-view")),
            },
        ],
    },

    {
        path: "/app/ranking",
        guard: AuthGuardNotAdmin,
        layout: DashboardLayout,
        routes: [
            {
                exact: true,
                path: "/app/ranking",
                component: () => <Redirect to="/coming" />,
            },
        ],
    },

    {
        path: "/app/reports",
        guard: AuthGuardNotAdmin,
        layout: DashboardLayout,
        routes: [
            {
                exact: true,
                path: "/app/reports",
                component: () => <Redirect to="/app/reports/dashboard" />,
            },
        ],
    },
    {
        path: "/app",
        guard: AuthGuardNotAdmin,
        layout: GlobalLayout,
        routes: [
            // Dashboard
            {
                exact: true,
                path: PATH_DASHBOARD,
                layout: HomePageLayout,
                component: lazy(() => import("src/views-ds/dashboard/dashboard-view")),
                betaFeatureConfig: [DASHBOARD_GLOBAL_TREND_BETA_CONFIG],
                meta: {
                    page: PageEnum.Dashboard,
                },
            },
            // Welcome Page
            {
                exact: true,
                path: PATH_WELCOME_PAGE,
                layout: HomePageLayout,
                component: lazy(() => import("src/views-ds/welcome/welcome-view")),
                meta: {
                    page: PageEnum.WelcomePage,
                },
            },

            // Offer
            {
                exact: true,
                path: PATH_OFFER,
                component: lazy(() => import("src/views-ds/offer/offer-tables-view/index")),
                betaFeatureConfig: [MARKETING_ACTION_TEMPLATE_BETA_CONFIG],
                meta: {
                    page: PageEnum.OfferManagement,
                },
            },

            // Sponsored Ads
            {
                exact: true,
                path: sponsoredAdsRoutes.tablesView(),
                component: lazy(
                    () => import("src/views-ds/sponsored-ad/sponsored-ads-tables-view")
                ),
                betaFeatureConfig: [MARKETING_ACTION_TEMPLATE_BETA_CONFIG],
                meta: {
                    page: PageEnum.SponsoredAdManagement,
                },
            },

            // Agenda
            {
                exact: true,
                path: PATH_AGENDA,
                component: lazy(() => import("src/views-ds/agenda/agenda-tab-view")),
                meta: {
                    page: PageEnum.OfferSchedule,
                },
            },

            {
                exact: true,
                path: "/app",
                component: () => <Redirect to={PATH_HOME} />,
            },
            {
                component: () => <Redirect to="/404" />,
            },
        ],
    },
    {
        // TODO: DELETE THIS ROUTE
        path: "/domains",
        routes: [
            {
                component: () => <DomainDemo />,
            },
        ],
    },
    {
        path: "*",
        routes: [
            {
                component: () => <Redirect to="/404" />,
            },
        ],
    },
];

export default routes;
